import React from "react";
import { motion } from "framer-motion";
import { Factory } from "lucide-react";

/* =====================================================================
   INSIDE LIFTILE — premium masonry gallery.
   Each tile has either a real photo (`image`) or a stylized placeholder
   (`icon`). Layout uses CSS columns so the page reads as true masonry.
   To swap a placeholder for a real photo later, just add `image: "/path"`
   and remove `icon` — no layout changes required.
   ===================================================================== */

const TILES = [
  {
    label: "Factory Exterior",
    image: "/about-header-factory.png",
    aspect: "aspect-[4/3]",
  },
  {
    label: "Workshop",
    image: "/gallery-workshop-interior.png",
    aspect: "aspect-[3/4]",
  },
  {
    label: "Precision Fabrication",
    image: "/process/step-03-precision-fabrication.png",
    aspect: "aspect-[4/3]",
  },
  {
    label: "Welding & Assembly",
    image: "/process/step-04-welding-assembly.png",
    aspect: "aspect-square",
  },
  {
    label: "Component Assembly",
    image: "/process/step-07-component-assembly.png",
    aspect: "aspect-[3/4]",
  },
  {
    label: "Powder Coating",
    image: "/process/step-06-powder-coating.png",
    aspect: "aspect-[4/3]",
  },
  {
    label: "Performance Testing",
    image: "/process/step-09-performance-testing.png",
    aspect: "aspect-square",
  },
  {
    label: "Packaging & Dispatch",
    image: "/process/step-10-packaging-dispatch.png",
    aspect: "aspect-[4/3]",
  },
  {
    label: "Office",
    image: "/gallery-office.png",
    aspect: "aspect-[3/4]",
  },
];

function GalleryTile({ tile, index }) {
  const Icon = tile.icon || Factory;
  return (
    <motion.figure
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: "easeOut", delay: (index % 3) * 0.08 }}
      data-testid={`gallery-tile-${index}`}
      className="group relative mb-5 md:mb-6 break-inside-avoid rounded-[20px] overflow-hidden bg-gradient-to-br from-[#181818] via-[#0d0d0d] to-[#000] shadow-[0_10px_28px_-10px_rgba(0,0,0,0.18)] hover:shadow-[0_22px_55px_-12px_rgba(255,107,26,0.32)] transition-shadow duration-500"
    >
      <div className={`relative ${tile.aspect} w-full overflow-hidden`}>
        {tile.image ? (
          <>
            <img
              src={tile.image}
              alt={tile.label}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-liftile-black/55 via-transparent to-transparent"
            />
          </>
        ) : (
          <>
            {/* Blueprint grid texture */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.1]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            {/* Orange ambient glow */}
            <div
              aria-hidden
              className="absolute -bottom-16 -right-16 w-[260px] h-[260px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,107,26,0.32) 0%, rgba(255,107,26,0) 70%)",
                filter: "blur(40px)",
              }}
            />
            {/* Centered icon */}
            <div className="absolute inset-0 grid place-items-center transition-transform duration-700 ease-out group-hover:scale-105">
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-liftile-orange/15 blur-2xl"
                />
                <div className="relative grid place-items-center w-20 h-20 md:w-24 md:h-24 rounded-full border border-liftile-orange/50">
                  <Icon className="w-9 h-9 md:w-11 md:h-11 text-liftile-orange" strokeWidth={1.4} />
                </div>
              </div>
            </div>
            {/* Real-photo-soon corner badge */}
            <div className="absolute top-4 right-4">
              <span
                data-testid={`gallery-tile-${index}-pending`}
                className="font-heading text-[9px] uppercase tracking-[0.22em] text-liftile-orange/85 border border-liftile-orange/45 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-sm"
              >
                Real Photo Soon
              </span>
            </div>
          </>
        )}

        {/* Soft orange glow on hover */}
        <div
          aria-hidden
          className="absolute inset-0 ring-0 group-hover:ring-2 group-hover:ring-liftile-orange/60 transition-all duration-500 rounded-[20px] pointer-events-none"
        />
      </div>

      {/* Caption */}
      <figcaption className="absolute bottom-4 left-5 right-5 flex items-center gap-3">
        <span className="block w-8 h-px bg-liftile-orange" />
        <span className="font-heading text-xs md:text-sm uppercase tracking-wider text-liftile-cream">
          {tile.label}
        </span>
      </figcaption>
    </motion.figure>
  );
}

export default function InsideLiftileGallery() {
  return (
    <section
      data-testid="about-inside-liftile"
      className="relative bg-liftile-cream py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="heading-eyebrow mb-4 text-liftile-orange">
            Our Manufacturing Facility
          </div>
          <h2
            data-testid="inside-liftile-heading"
            className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
          >
            Inside <span className="text-liftile-orange">LIFTILE</span>
          </h2>
          <p className="font-body text-liftile-matte-black/70 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
            Explore the environment, craftsmanship and engineering processes
            behind every LIFTILE machine.
          </p>

          {/* Trust statement w/ orange vertical accent */}
          <div className="mt-7 max-w-2xl border-l-2 border-liftile-orange pl-5">
            <p className="font-body italic text-sm md:text-[0.95rem] text-liftile-matte-black/70 leading-relaxed">
              Every image showcased throughout this page is captured from our
              real manufacturing facility, production processes and project
              installations — reflecting our commitment to authenticity,
              transparency and engineering excellence.
            </p>
          </div>
        </motion.div>

        {/* Masonry — CSS columns */}
        <div
          data-testid="inside-liftile-masonry"
          className="mt-14 md:mt-20 columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6 [column-fill:_balance]"
        >
          {TILES.map((tile, i) => (
            <GalleryTile key={tile.label} tile={tile} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
